import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import GeoMeasurementReport from '../components/GeoMeasurementReport';
import GeoSuggestions from '../components/GeoSuggestions';
import DateRangeBar from '../components/DateRangeBar';
import { presetRange, longDate } from '../dateRange';

const ENGINE_LABELS = { chat_gpt: 'ChatGPT', google_ai_overview: 'Google AI Overviews' };
const SOURCE_LABELS = { gsc: 'Search Console', ga4: 'Google Analytics', bing: 'Bing Webmaster' };
const COLLECT_STATUS = { not_connected: 'לא מחובר לאתר', error: 'שגיאה' };

const STATES = {
  both: { label: 'אוזכר וצוטט', color: 'var(--green)', fill: true },
  mentioned: { label: 'אוזכר בטקסט', color: 'var(--green)', fill: false },
  cited: { label: 'צוטט במקורות', color: 'var(--accent)', fill: true },
  none: { label: 'לא הופיע', color: 'var(--border-light)', fill: false },
  no_aio: { label: 'אין AI Overview', color: 'var(--border)', fill: false, dashed: true },
  error: { label: 'שגיאה', color: 'var(--red)', fill: false },
};

function shortDate(iso) {
  if (!iso) return '-';
  var d = new Date(iso + 'Z');
  return d.getDate() + '/' + (d.getMonth() + 1);
}

function Dot({ state, onClick }) {
  var s = STATES[state];
  if (!s) return <span style={{ color: 'var(--text-muted)' }}>·</span>;
  return (
    <span title={s.label} onClick={onClick} style={{
      display: 'inline-block', width: 14, height: 14, borderRadius: '50%', cursor: onClick ? 'pointer' : 'default',
      border: '2px ' + (s.dashed ? 'dashed ' : 'solid ') + s.color, background: s.fill ? s.color : 'transparent',
    }} />
  );
}

function Count({ label, part, whole, first }) {
  return (
    <div className="section-card" style={{ padding: '14px 16px', marginBottom: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--mono)' }}>
        {part}<span style={{ fontSize: 14, color: 'var(--text-muted)' }}>/{whole}</span>
      </div>
      {first != null && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>בריצה הראשונה: {first.part}/{first.whole}</div>
      )}
    </div>
  );
}

export default function GeoMeasurement() {
  const { id: siteId } = useParams();
  const [tab, setTab] = useState('overview');
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState({});
  const [engine, setEngine] = useState('chat_gpt');
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [brandTerms, setBrandTerms] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [range, setRange] = useState(() => presetRange('all'));
  const [reloading, setReloading] = useState(false);
  const initialized = useRef(null);

  const load = useCallback(async () => {
    try {
      const [proj, pr, res, met, rep] = await Promise.all([
        api.getGeoProject(siteId), api.getGeoPrompts(siteId),
        // Within a date range, show every run in it (up to the API's cap), not just the last 12.
        api.getGeoResults(siteId, range.start ? 52 : null, range), api.getGeoMetrics(siteId, 1),
        api.getGeoReport(siteId, range),
      ]);
      setCoverage(met.coverage || {});
      setReport(rep);
      setProject(proj);
      setPrompts(pr.prompts || []);
      setData(res);
      return proj;
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      return null;
    }
  }, [siteId, range]);

  useEffect(() => {
    setReloading(true);
    load().then(proj => {
      setReloading(false);
      // The settings fields are filled once per site, not on every date change.
      if (!proj || initialized.current === siteId) return;
      initialized.current = siteId;
      setBrandTerms((proj.brand_terms || []).join(', '));
      setActivityDate(proj.activity_start_date || '');
      if (!proj.active_prompts) setTab('settings');
    });
  }, [load, siteId]);

  // While a run is in flight, refresh until it finishes.
  const live = project && project.live_run;
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [live, load]);

  async function act(fn, successText) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (successText) setMessage({ type: 'success', text: successText });
      await load();
    } catch (err) { setMessage({ type: 'error', text: err.message }); }
    setBusy(false);
  }

  function runNow() {
    const calls = prompts.length * (project.engines || []).length;
    if (!window.confirm('להריץ עכשיו ' + calls + ' בדיקות בתשלום (' + prompts.length + ' פרומפטים × ' + project.engines.length + ' מנועים)?')) return;
    act(() => api.runGeoMeasurement(siteId), 'הריצה התחילה — התוצאות יתעדכנו כאן בדקות הקרובות');
  }

  function saveSettings(extra) {
    const payload = {
      brand_terms: brandTerms.split(',').map(t => t.trim()).filter(Boolean),
      ...(activityDate ? { activity_start_date: activityDate } : { clear_activity_start_date: true }),
      ...extra,
    };
    act(() => api.updateGeoProject(siteId, payload), 'נשמר');
  }

  function toggleEngine(e) {
    const current = project.engines || [];
    const next = current.includes(e) ? current.filter(x => x !== e) : current.concat(e);
    if (!next.length) return;
    act(() => api.updateGeoProject(siteId, { engines: next }));
  }

  async function suggest() {
    setBusy(true);
    setMessage(null);
    try {
      const want = Math.max(1, Math.min(20, project.max_prompts - prompts.length));
      const res = await api.suggestGeoPrompts(siteId, want);
      const lines = (res.prompts || []).map(p => p.prompt + (p.search_query ? ' | ' + p.search_query : ''));
      setDraft(prev => (prev.trim() ? prev.trim() + '\n' : '') + lines.join('\n'));
    } catch (err) { setMessage({ type: 'error', text: 'שגיאה ביצירת הצעות: ' + err.message }); }
    setBusy(false);
  }

  function addDraft() {
    const items = draft.split('\n').map(line => {
      const parts = line.split('|');
      return { prompt: (parts[0] || '').trim(), search_query: (parts[1] || '').trim() || null };
    }).filter(p => p.prompt);
    if (!items.length) return;
    act(async () => { await api.addGeoPrompts(siteId, items); setDraft(''); });
  }

  function retire(p) {
    if (!window.confirm('להוציא את הפרומפט מהסט? התוצאות שנאספו נשמרות, אבל הוא לא ייבדק יותר.')) return;
    act(() => api.retireGeoPrompt(p.id));
  }

  function purge(p) {
    const results = p.checks_count
      ? 'יימחקו לצמיתות גם ' + p.checks_count + ' תוצאות שנאספו עבורו.'
      : 'עדיין לא נאספו עבורו תוצאות.';
    if (!window.confirm('למחוק את הפרומפט? ' + results + ' אי אפשר לבטל את הפעולה.')) return;
    act(() => api.purgeGeoPrompt(p.id));
  }

  function saveWording() {
    const p = prompts.find(x => x.id === editing.id);
    const text = editing.text.trim();
    if (!p || !text || text === p.prompt) { setEditing(null); return; }
    act(async () => {
      await api.updateGeoPrompt(p.id, { prompt: text, search_query: p.search_query, topic: p.topic });
      setEditing(null);
    });
  }

  function saveSearchQuery(p, value) {
    if ((p.search_query || '') === value.trim()) return;
    act(() => api.updateGeoPrompt(p.id, { prompt: p.prompt, search_query: value, topic: p.topic }));
  }

  async function openCheck(checkId) {
    try { setDetail(await api.getGeoCheck(checkId)); }
    catch (err) { setMessage({ type: 'error', text: err.message }); }
  }

  if (!project || !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{message ? message.text : 'טוען...'}</div>;
  }

  const runs = data.runs || [];
  const engines = project.engines || [];
  const shownEngine = engines.includes(engine) ? engine : engines[0];
  const withData = runs.filter(r => r.summary && r.summary[shownEngine]);
  const first = withData.length ? withData[0].summary[shownEngine] : null;
  const last = withData.length ? withData[withData.length - 1].summary[shownEngine] : null;
  const firstOf = (key, wholeKey) => (withData.length > 1 ? { part: first[key], whole: first[wholeKey] } : null);
  const cellMap = {};
  (data.cells || []).forEach(c => { cellMap[c.prompt_id + ':' + c.run_id + ':' + c.platform] = c; });

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>מדידת GEO</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            {project.enabled
              ? 'מדידה שבועית פעילה' + (project.measurement_started_at ? ' מאז ' + shortDate(project.measurement_started_at.slice(0, 19)) : '')
              : 'המדידה השבועית כבויה'}
            {' · '}{prompts.length} פרומפטים · עלות מצטברת ${project.total_cost}
          </p>
        </div>
        <button className="btn btn-primary" disabled={busy || !!live || !prompts.length} onClick={runNow}>
          {live ? 'ריצה מתבצעת...' : 'הרצה עכשיו'}
        </button>
      </div>

      {message && (
        <div style={{
          background: message.type === 'error' ? 'var(--red-glow)' : 'var(--green-glow)',
          border: '1px solid ' + (message.type === 'error' ? 'var(--red)' : 'var(--green)'),
          borderRadius: 'var(--radius-sm)', padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: message.type === 'error' ? 'var(--red)' : 'var(--green)',
        }}>{message.text}</div>
      )}

      <div className="filter-row" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={'filter-chip' + (tab === 'overview' ? ' active' : '')} onClick={() => setTab('overview')}>סקירה</button>
        <button className={'filter-chip' + (tab === 'results' ? ' active' : '')} onClick={() => setTab('results')}>בדיקה ישירה</button>
        <button className={'filter-chip' + (tab === 'settings' ? ' active' : '')} onClick={() => setTab('settings')}>הגדרות ופרומפטים</button>
      </div>

      {(tab === 'overview' || tab === 'results') && (
        <DateRangeBar value={range} onChange={setRange}
          presets={['all', 28, 90, 180, 365, 'this_month', 'last_month']}
          note={(reloading ? 'מעדכן… ' : '') + (tab === 'overview'
            ? 'הטווח חל על הגרפים, הדומיינים והמרות לפי אירוע. השוואות ה-Baseline מול עכשיו תמיד מחושבות על כל הנתונים.'
            : 'מוצגות הריצות שבוצעו בטווח שנבחר.')} />
      )}

      {tab === 'overview' && report && <GeoMeasurementReport report={report} />}

      {tab === 'results' && (
        <div>
          {engines.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {engines.map(e => (
                <button key={e} className={'filter-chip' + (shownEngine === e ? ' active' : '')} onClick={() => setEngine(e)}>{ENGINE_LABELS[e] || e}</button>
              ))}
            </div>
          )}

          {!last ? (
            <div className="section-card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {range.start ? 'אין ריצות בטווח התאריכים שנבחר.'
                : live ? 'הריצה הראשונה מתבצעת.' : 'עדיין אין ריצות. הריצה הראשונה היא נקודת ה-Baseline.'}
            </div>
          ) : (
            <React.Fragment>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 8 }}>
                {shownEngine === 'google_ai_overview' && (
                  <Count label="הופיע AI Overview" part={last.aio_present} whole={last.ok} first={firstOf('aio_present', 'ok')} />
                )}
                <Count label="אוזכר בטקסט התשובה" part={last.mentioned} whole={last.ok} first={firstOf('mentioned', 'ok')} />
                <Count label="צוטט במקורות" part={last.cited} whole={last.ok} first={firstOf('cited', 'ok')} />
                <Count label="נראה (אזכור או ציטוט)" part={last.visible} whole={last.ok} first={firstOf('visible', 'ok')} />
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 16px' }}>
                {range.start ? 'הריצה האחרונה בטווח' : 'הריצה האחרונה'} ({longDate(withData[withData.length - 1].started_at)}), מתוך הבדיקות שהחזירו תשובה
                {last.errors ? ' · ' + last.errors + ' שגיאות לא נספרות' : ''}. שינוי של פרומפט אחד או שניים בין ריצות הוא בתחום הרעש.
              </p>

              <div className="section-card" style={{ padding: 16, overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 11, color: 'var(--text-secondary)' }}>
                  {Object.keys(STATES).filter(k => shownEngine === 'google_ai_overview' || k !== 'no_aio').map(k => (
                    <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Dot state={k} />{STATES[k].label}</span>
                  ))}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)' }}>פרומפט</th>
                      {runs.map(r => (
                        <th key={r.id} title={longDate(r.started_at)} style={{ padding: '6px 4px', fontWeight: 500, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>{shortDate(r.started_at)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.prompts || []).map(p => (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--border)', opacity: p.active ? 1 : 0.5 }}>
                        <td style={{ padding: '8px', minWidth: 260 }}>
                          {p.prompt}
                          {shownEngine === 'google_ai_overview' && p.search_query && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.search_query}</div>
                          )}
                        </td>
                        {runs.map(r => {
                          const cell = cellMap[p.id + ':' + r.id + ':' + shownEngine];
                          return (
                            <td key={r.id} style={{ textAlign: 'center', padding: '8px 4px' }}>
                              {cell ? <Dot state={cell.state} onClick={() => openCheck(cell.check_id)} /> : <Dot />}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          )}

          {detail && (
            <div className="section-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                  {ENGINE_LABELS[detail.platform] || detail.platform}{detail.model_name ? ' · ' + detail.model_name : ''}
                </h3>
                <button className="btn-sm" onClick={() => setDetail(null)}>סגירה</button>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{detail.query}</div>
              {detail.status === 'error' ? (
                <div style={{ fontSize: 13, color: 'var(--red)' }}>שגיאה: {detail.error}</div>
              ) : (
                <React.Fragment>
                  {detail.mention_text && (
                    <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 8 }}>אזכור: {detail.mention_text}</div>
                  )}
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)', maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
                    {detail.raw_answer || (detail.aio_present === false ? 'לא הופיע AI Overview לשאילתה הזו.' : '')}
                  </div>
                  {(detail.sources || []).length > 0 && (
                    <ol style={{ fontSize: 12, margin: 0, paddingInlineStart: 20 }}>
                      {detail.sources.map((s, i) => (
                        <li key={i} style={{ marginBottom: 3, fontWeight: (detail.own_urls || []).includes(s.url) ? 700 : 400 }}>
                          <a href={s.url} target="_blank" rel="noopener noreferrer" dir="ltr" style={{ color: 'var(--accent)' }}>{s.title || s.url}</a>
                        </li>
                      ))}
                    </ol>
                  )}
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div>
          <div className="section-card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 14px' }}>הגדרות מדידה</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={project.enabled} disabled={busy}
                onChange={e => saveSettings({ enabled: e.target.checked })} style={{ accentColor: 'var(--accent)' }} />
              מדידה שבועית אוטומטית (כל יום שני)
            </label>
            <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
              {(project.available_engines || []).map(e => (
                <label key={e} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={engines.includes(e)} disabled={busy} onChange={() => toggleEngine(e)} style={{ accentColor: 'var(--accent)' }} />
                  {ENGINE_LABELS[e] || e}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>שמות נוספים של המותג, מופרדים בפסיק (שם האתר במערכת נבדק תמיד)</div>
            <input className="geo-input" value={brandTerms} onChange={e => setBrandTerms(e.target.value)} style={{ width: '100%', marginBottom: 14 }} />
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>תאריך תחילת פעילות ה-GEO (אופציונלי)</div>
            <input className="geo-input" type="date" value={activityDate} onChange={e => setActivityDate(e.target.value)} style={{ marginBottom: 14 }} />
            <div><button className="btn btn-primary" disabled={busy} onClick={() => saveSettings()}>שמירה</button></div>
          </div>

          <div className="section-card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>נתוני חיפוש ותנועה</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              נשמרים כל יום לאתרים שהמדידה פעילה בהם, ללא עלות. מילוי לאחור מושך את 90 הימים האחרונים.
            </p>
            {Object.keys(SOURCE_LABELS).map(source => {
              const stored = coverage[source];
              const last = (project.search_collect_outcome || {})[source];
              return (
                <div key={source} style={{ display: 'flex', gap: 10, fontSize: 13, marginBottom: 6 }}>
                  <span style={{ width: 130, color: 'var(--text-secondary)' }}>{SOURCE_LABELS[source]}</span>
                  <span>{stored ? stored.days + ' ימים שמורים (' + shortDate(stored.first) + ' עד ' + shortDate(stored.last) + ')' : 'אין נתונים שמורים'}</span>
                  {last && last.status !== 'ok' && (
                    <span style={{ color: last.status === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>
                      {COLLECT_STATUS[last.status] || last.status}{last.error ? ' — ' + last.error : ''}
                    </span>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-secondary" disabled={busy}
                onClick={() => act(() => api.backfillGeoMetrics(siteId), 'המילוי לאחור התחיל — רעננו בעוד דקה')}>מילוי לאחור</button>
            </div>
          </div>

          <div className="section-card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>סט הפרומפטים ({prompts.length}/{project.max_prompts})</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
              עריכת ניסוח שומרת על ההיסטוריה של הפרומפט, ולכן מתאימה לתיקון ולא להחלפת השאלה. "הוצאה" מפסיקה לבדוק אותו ושומרת את התוצאות; "מחיקה" מוחקת גם אותן.
            </p>
            {prompts.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                <tbody>
                  {prompts.map(p => (
                    <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 4px' }}>
                        {editing && editing.id === p.id ? (
                          <input className="geo-input" autoFocus value={editing.text} maxLength={500}
                            onChange={e => setEditing({ id: p.id, text: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') saveWording(); if (e.key === 'Escape') setEditing(null); }}
                            style={{ width: '100%' }} />
                        ) : p.prompt}
                      </td>
                      <td style={{ padding: '8px 4px', width: 220 }}>
                        <input className="geo-input" defaultValue={p.search_query || ''} placeholder="ביטוי חיפוש ל-AIO"
                          onBlur={e => saveSearchQuery(p, e.target.value)} style={{ width: '100%' }} />
                      </td>
                      <td style={{ padding: '8px 4px', width: 190, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {editing && editing.id === p.id ? (
                          <span style={{ display: 'inline-flex', gap: 4 }}>
                            <button className="btn-sm" disabled={busy} onClick={saveWording}>שמירה</button>
                            <button className="btn-sm" disabled={busy} onClick={() => setEditing(null)}>ביטול</button>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: 4 }}>
                            <button className="btn-sm" disabled={busy} onClick={() => setEditing({ id: p.id, text: p.prompt })}>עריכה</button>
                            <button className="btn-sm" disabled={busy} onClick={() => retire(p)}>הוצאה</button>
                            <button className="btn-sm" disabled={busy || !!live} onClick={() => purge(p)} style={{ color: 'var(--red)' }}>מחיקה</button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginBottom: 16 }}><GeoSuggestions siteId={siteId} onChanged={load} /></div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>הוספה — שורה לכל פרומפט, בפורמט: שאלה | ביטוי חיפוש קצר (אופציונלי)</div>
            <textarea className="geo-input" rows={8} value={draft} onChange={e => setDraft(e.target.value)} style={{ width: '100%', marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={busy || !draft.trim()} onClick={addDraft}>הוספה לסט</button>
              <button className="btn btn-secondary" disabled={busy || prompts.length >= project.max_prompts} onClick={suggest}>הצעת פרומפטים ממילות המפתח</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
